import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  ProjectDashboardResponse,
  ProjectDeleteResponse,
  ProjectPayload,
  ProjectsListResponse,
  ProjectSingleResponse,
} from './interfaces/projects.interface'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class ProjectsApiService {
  private static _instance: ProjectsApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): ProjectsApiService {
    if (!ProjectsApiService._instance) {
      ProjectsApiService._instance = new ProjectsApiService()
    }

    return ProjectsApiService._instance
  }

  public static refresh(): void {
    ProjectsApiService._instance = new ProjectsApiService()
  }

  public static async getList(): Promise<ProjectsListResponse> {
    const response =
      await ProjectsApiService.instance.client.get<ProjectsListResponse>(
        '/v1_private/projects',
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getById(id: string): Promise<ProjectSingleResponse> {
    const response =
      await ProjectsApiService.instance.client.get<ProjectSingleResponse>(
        `/v1_private/projects/${id}`,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getDashboard(
    id: string,
    fromDate?: string,
    toDate?: string
  ): Promise<ProjectDashboardResponse> {
    const response =
      await ProjectsApiService.instance.client.get<ProjectDashboardResponse>(
        `/v1_private/projects/${id}/dashboard`,
        { headers: getHeaders(), params: { fromDate, toDate } }
      )
    return response.data
  }

  public static async create(
    payload: ProjectPayload
  ): Promise<ProjectsListResponse> {
    const response =
      await ProjectsApiService.instance.client.post<ProjectSingleResponse>(
        '/v1_private/projects',
        payload,
        { headers: getHeaders() }
      )
    return {
      data: [response.data.data],
      success: response.data.success,
    }
  }

  public static async update(
    id: string,
    payload: ProjectPayload
  ): Promise<ProjectSingleResponse> {
    const response =
      await ProjectsApiService.instance.client.patch<ProjectSingleResponse>(
        `/v1_private/projects/${id}`,
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async delete(id: string): Promise<ProjectDeleteResponse> {
    const response =
      await ProjectsApiService.instance.client.delete<ProjectDeleteResponse>(
        `/v1_private/projects/${id}`,
        { headers: getHeaders() }
      )
    return response.data
  }
}

export { ProjectsApiService }
